import { Link } from "react-router-dom";

const toc = [
  { id: "sec-1", label: "一句「再见」的分量" },
  { id: "sec-2", label: "三处被忽略的分镜" },
  { id: "sec-3", label: "遗憾，还是圆满？" },
];

function EndingAnalysis() {
  return (
    <section className="grid grid-cols-1 gap-10 lg:grid-cols-[1fr_240px]">
      <article className="rounded-lg border border-background-200/70 bg-background-50 p-6 md:p-8">
        <span className="inline-flex items-center gap-2 rounded-full bg-primary-500 px-3 py-1 text-xs font-medium text-background-50">
          结局专题
        </span>
        <h2 className="mt-4 font-heading text-xl font-bold leading-snug text-foreground-950 md:text-2xl">
          大结局深度解析：那句没说出口的再见
        </h2>
        <div className="mt-4 flex flex-wrap items-center gap-4 text-xs text-foreground-500">
          <span className="inline-flex items-center gap-1"><i className="ri-time-line"></i> 10 分钟阅读</span>
          <span>更新于 2026-08-12</span>
          <span className="inline-flex items-center gap-1"><i className="ri-heart-3-line text-primary-600"></i> 987 人赞</span>
        </div>

        <p className="mt-6 text-sm leading-7 text-foreground-700">
          结局播出后，读者群里一度「吵翻」。有人直呼意难平，有人却说这是全剧最圆满的一页。站在不同的阅读视角，得出的结论截然不同。今天我们放下情绪，从台词、分镜与伏笔三个角度，逐帧拆解那句「没说出口的再见」。
        </p>

        <h3 id="sec-1" className="mt-8 scroll-mt-24 font-heading text-lg font-bold text-foreground-950">
          一、一句「再见」的分量
        </h3>
        <p className="mt-4 text-sm leading-7 text-foreground-700">
          整部作品的主角阿浩，最大的课题从来不是「追到什么」，而是「学会告别」。当小雨在黄昏的站台轻声说出那句「有空再见」，看似轻描淡写，实则是把十年之约从「等待兑现」悄悄转向「好好收尾」。
        </p>
        <p className="mt-4 text-sm leading-7 text-foreground-700">
          作者在台词上做得很克制——没有大声嘶吼，没有铺垫的煽情配乐，只有一句普通到几乎会被错过的话。而正是这种「克制」，让告别显得格外真实。因为现实中真正的离别，大多就是这样静悄悄的。
        </p>

        <h3 id="sec-2" className="mt-8 scroll-mt-24 font-heading text-lg font-bold text-foreground-950">
          二、三处被忽略的分镜
        </h3>
        <p className="mt-4 text-sm leading-7 text-foreground-700">
          很多读者都低估了作者的分镜功力。比如结局前一话的「空桌」镜头——嘿咻猫咪往常趴着的那块桌角，第一次没有任何东西；再比如站台对面那盏忽明忽暗的路灯，恰好对应了开篇第一话的同一盏灯。
        </p>
        <p className="mt-4 text-sm leading-7 text-foreground-700">
          最精妙的是倒数第二格：阿浩回头时，画面里只有他自己的影子，而影子的方向，正好指向小雨离开的方向。这一格几乎没有台词，却把所有没说完的话都交代清楚了。
        </p>

        <h3 id="sec-3" className="mt-8 scroll-mt-24 font-heading text-lg font-bold text-foreground-950">
          三、遗憾，还是圆满？
        </h3>
        <p className="mt-4 text-sm leading-7 text-foreground-700">
          回到最初的问题：这个结局是遗憾还是圆满？我们的答案是——两者都是，而且这恰恰是它的高明之处。对「期待大团圆」的读者而言，那句没说出口的再见是遗憾；但回到全剧的核心主题「学会告别」，这恰恰是阿浩真正成长完成的证明。
        </p>
        <p className="mt-4 text-sm leading-7 text-foreground-700">
          作者想要讲述的，从来不是「有没有在一起」，而是「有没有好好告别」。从这个角度看，结局非但不烂尾，反而是对全剧主题最完整的收束。所谓圆满，不是没有缺憾，而是终于能与缺憾和解。
        </p>

        <div className="mt-8 rounded-lg bg-secondary-100 p-5">
          <p className="text-sm leading-relaxed text-secondary-900">
            <strong>一句话总结：</strong>那句「没说出口的再见」，是阿浩学会告别的证明，也是嘿咻嘿咻漫画把「遗憾」写成了「温柔圆满」的妙笔。
          </p>
        </div>

        <div className="mt-8 flex flex-wrap gap-3">
          {["结局考据", "深度解析", "伏笔"].map((t) => (
            <span key={t} className="rounded-full bg-accent-100 px-3 py-1 text-xs font-medium text-accent-800">
              {t}
            </span>
          ))}
        </div>
      </article>

      <aside className="hidden lg:block">
        <div className="sticky top-24 rounded-lg border border-background-200/70 bg-background-50 p-5">
          <h4 className="text-sm font-semibold text-foreground-950">本文目录</h4>
          <nav className="mt-4 space-y-2">
            {toc.map((t) => (
              <a
                key={t.id}
                href={`#${t.id}`}
                className="block rounded-md px-3 py-2 text-sm text-foreground-700 transition-colors hover:bg-primary-100 hover:text-primary-800"
              >
                {t.label}
              </a>
            ))}
          </nav>
          <div className="mt-5 border-t border-background-200 pt-4">
            <p className="text-xs text-foreground-500">还想看更多？</p>
            <Link to="/reviews" className="mt-2 inline-flex items-center gap-1 text-sm font-medium text-primary-700 hover:text-primary-800">
              去读者讨论 <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </div>
      </aside>
    </section>
  );
}

export default EndingAnalysis;